import get from 'lodash/get';
import find from 'lodash/find';
import filter from 'lodash/filter';

import clientMixins from 'utils/client-mixins';
import store from 'store';
import { privileges } from 'domains/base/_base-enum';

class NavigationHelper {
  constructor() {
    this.navRoot = null;
    this.navLinks = [];
    this.showBackButton = false;
    this.backPath = null;
    this.campaignsBackPath = null;
  }

  _setNav(root, title, links = [], backPath = '/') {
    this.navRoot = root;
    document.title = `Viralstyle${title ? ` - ${title}` : ''}`;
    this.navLinks = links;
    this.showBackButton = backPath !== '/' && backPath;
    this.backPath = backPath;
  }

  setCampaignsBackPath(path) {
    this.campaignsBackPath = path;
  }

  routeChanged(route, fromPath, userRoleInfo) {
    this.showBackButton = false;

    this.routes(route.matched, route.params, fromPath, userRoleInfo);
  }

  routes(matched, params, fromPath, userRoleInfo) {
    switch (get(matched, '[0].name')) {
      case 'campaigns':
        this.campaignRoutes(matched, params, fromPath, userRoleInfo);
        break;
      case 'orders':
        this.orderRoutes(matched, fromPath);
        break;
      case 'promotions':
        this.promotionRoutes(matched, fromPath);
        break;
      case 'stores':
        this.storesRoutes(matched, fromPath);
        break;
      case 'settings':
        this.settingsRoutes(matched, userRoleInfo);
        break;
      case 'domains':
        this.whitelabelRoutes(matched, fromPath);
        break;
      default:
        this.errorRoutes(matched);
        break;
    }
  }

  campaignRoutes(matched, params, fromPath, userRoleInfo) {
    let backPath = this.campaignsBackPath || '/campaigns',
      routes = [];

    if (clientMixins.methods.hasPrivilege(privileges.CAMPAIGN_DASHBOARD, userRoleInfo.role)) routes.push('Analytics');
    if (clientMixins.methods.hasPrivilege(privileges.CAMPAIGN_DETAILS, userRoleInfo.role)) routes.push('Details');
    if (clientMixins.methods.hasPrivilege(privileges.CAMPAIGN_ORDERS, userRoleInfo.role)) routes.push('Orders');
    if (clientMixins.methods.hasPrivilege(privileges.CAMPAIGN_PROMOTIONS, userRoleInfo.role)) routes.push({ key: 'Promotions', text: 'Coupons' });
    if (clientMixins.methods.hasPrivilege(privileges.CAMPAIGN_MARKETING, userRoleInfo.role)) routes.push('Marketing');
    if (clientMixins.methods.hasPrivilege(privileges.CAMPAIGN_ASSETS, userRoleInfo.role)) routes.push('Assets');
    if (clientMixins.methods.hasPrivilege(privileges.CAMPAIGN_RELAUNCH_DASHBOARD, userRoleInfo.role)) routes.push('Relaunches');

    switch (get(matched, '[2].name')) {
      case 'campaignsDetails':
        this._setNav('campaigns', 'Campaign Details', routes, backPath);
        break;
      case 'campaignsMarketing':
        this._setNav('campaigns', 'Campaign Marketing', routes, backPath);
        break;
      case 'campaignsAnalytics':
        this._setNav('campaigns', 'Campaign Analytics', routes, backPath);
        break;
      case 'campaignsOrders':
        this.campaignOrdersRoutes(matched, params, backPath, routes);
        break;
      case 'campaignsPromotions':
        this.campaignPromotionRoutes(matched, params, backPath, routes);
        break;
      case 'campaignsAssets':
        this._setNav('campaigns', 'Campaign Assets', routes, backPath);
        break;
      case 'campaignsRelaunches':
        this._setNav('campaigns', 'Campaign Relaunches', routes, backPath);
        break;
      case 'campaignsAds':
        this._setNav('campaigns', 'Campaign ADs', routes, backPath);
        break;
      default:
        this._setNav('campaigns', 'Campaigns', [{ key: 'All', text: 'Campaigns' }, 'Drafts']);
        break;
    }
  }

  campaignOrdersRoutes(matched, params, campaignsBackPath = 'campaigns', routes) {
    let backPath = { name: 'campaignsOrdersAll', params };

    switch (get(matched, '[3].name')) {
      case 'campaignsOrdersDetails':
        this._setNav('campaigns', 'Campaign Order Details', routes, backPath);
        break;
      default:
        this._setNav('campaigns', 'Campaign Orders', routes, campaignsBackPath);
        break;
    }
  }

  campaignPromotionRoutes(matched, params, campaignsBackPath = 'campaigns', routes) {
    let backPath = { name: 'campaignsPromotionsAll', params };

    switch (get(matched, '[3].name')) {
      case 'campaignsPromotionsEdit':
        this._setNav('campaigns', 'Edit Campaign Coupon', [], backPath);
        break;
      case 'campaignsPromotionsCreate':
        this._setNav('campaigns', 'Create Campaign Coupon', [], backPath);
        break;
      default:
        this._setNav('campaigns', 'Campaign Coupons', routes, campaignsBackPath);
        break;
    }
  }

  orderRoutes(matched, fromPath) {
    let backPath = fromPath || '/orders';

    switch (get(matched, '[1].name')) {
      case 'ordersDetails':
        this._setNav('orders', 'Order Details', [], backPath);
        break;
      default:
        this._setNav('orders', 'Orders', []);
        break;
    }
  }

  promotionRoutes(matched, fromPath) {
    let backPath = '/coupons';

    switch (get(matched, '[1].name')) {
      case 'promotionsEdit':
        this._setNav('promotions', 'Edit Coupon', [], backPath);
        break;
      case 'promotionsCreate':
        this._setNav('promotions', 'Create Coupon', [], backPath);
        break;
      default:
        this._setNav('promotions', 'Coupons', []);
        break;
    }
  }

  storesRoutes(matched, fromPath) {
    let backPath = fromPath || '/collections';

    if (fromPath === '/collections/create' && get(matched, `[${matched.length - 1}].name`) === 'storesEdit') backPath = '/collections';

    switch (get(matched, '[1].name')) {
      case 'storesEdit':
        this._setNav('stores', 'Edit Collection', [], backPath);
        break;
      case 'storesCreate':
        this._setNav('stores', 'Create Collection', [], backPath);
        break;
      default:
        this._setNav('stores', 'Collections', []);
        break;
    }
  }

  settingsRoutes(matched, userRoleInfo) {
    let routes = [];

    if (clientMixins.methods.hasPrivilege(privileges.SETTINGS_ACCOUNT, userRoleInfo.role) || clientMixins.methods.hasPrivilege(privileges.SETTINGS_ACCOUNT_NOTIFICATIONS, userRoleInfo.role)) {
      routes.push('Account');
    }

    if (clientMixins.methods.hasPrivilege(privileges.SETTINGS_USERS, userRoleInfo.role)) {
      routes.push('Users');
    }

    if (clientMixins.methods.hasPrivilege(privileges.SETTINGS_TRACKING, userRoleInfo.role)) {
      routes.push('Tracking');
    }

    if (clientMixins.methods.hasPrivilege(privileges.SETTINGS_PAYMENT, userRoleInfo.role)) {
      routes.push('Payment');
    }

    if (get(userRoleInfo.permissions, 'payouts.view')) {
      routes.push('Payouts');
    }

    if (clientMixins.methods.hasPrivilege(privileges.SETTINGS_EMAILS, userRoleInfo.role)) {
      routes.push('Emails');
    }

    if (clientMixins.methods.hasPrivilege(privileges.SETTINGS_ACCOUNT, userRoleInfo.role)) {
      routes.push('Security');
    }

    this._setNav('settings', 'Settings', routes);
  }

  whitelabelRoutes(matched, fromPath) {
    let activeDomain = find(store.state.whitelabel.domains, domain => domain.active),
      domainsEditBackPath = get(store.state.whitelabel, 'domains.length') > 1 || !activeDomain ? '/domains/all' : null,
      domainsRegisterBackPath = get(store.state.whitelabel, 'domains.length') ? '/domains/all' : null;

    switch (get(matched, '[1].name')) {
      case 'domainsList':
        this._setNav('domainsList', 'Domains', []);
        break;
      case 'domainsRegister':
        this._setNav('domainsRegister', 'Domain Register', [], domainsRegisterBackPath);
        break;
      case 'domainsCreate':
        this._setNav('domainsCreate', 'Domain Create', [], '/domains/register');
        break;
      case 'domainsEdit':
        this._setNav('domainsEdit', 'Edit Domain', [], domainsEditBackPath);
        break;
      default:
        this._setNav('domain', 'Domain', []);
        break;
    }
  }

  errorRoutes(matched) {
    // TODO: error routes
    this.navLinks = [];
    document.title = 'Viralstyle - Error';
  }
}

export default new NavigationHelper();